import React, { useCallback, useMemo, Fragment } from 'react';
import classNames from 'classnames';
import {
    useTheme,
    FieldGroup,
    Icon,
    Input,
    Section,
    Select,
    Switch,
    Text,
    Tooltip,
} from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { getCrossID } from 'utils/filters';
import { getRange } from 'utils/time';
import FiltersSection from '../FiltersSection';
import {
    defaultPlaceholders,
    defaultValues,
    fieldsComponent,
    inputFields,
    rangeFields,
    customFieldsComponent,
} from '../constants';
import { operators } from '../operators';
import useAdvancedFilters from './useAdvancedFilters';
import FilterTitle from '../FilterTitle';
const {
    EMPTY,
    NOT_EMPTY,
    GREATER,
    GREATER_OR_EQUAL,
    LESS,
    LESS_OR_EQUAL,
    TODAY,
    YESTERDAY,
    TOMORROW,
    THIS_WEEK,
    LAST_WEEK,
    NEXT_WEEK,
    THIS_MONTH,
    LAST_MONTH,
    NEXT_MONTH,
    THIS_QUARTER,
    LAST_QUARTER,
    NEXT_QUARTER,
    THIS_YEAR,
    LAST_YEAR,
    NEXT_YEAR,
} = operators;

const useFilters = ({
    classes = {},
    crossOperators,
    entity,
    getAllowedOperators = () => {},
    getFieldsWithoutAdvancedFilters = () => {},
    getHasAdvancedFilters = () => {},
    isPinnable,
    onToggle,
    toggleQuickFilter,
    operators,
    schema,
    toggled,
    useSections,
    values,
    fromQuickFilters = false,
}) => {
    const { language, t } = useWeb5Context();
    const theme = useTheme();

    const { getFieldOperator, getOperatorValues, hasAdvancedFilters } = useAdvancedFilters({
        operators,
        crossOperators,
        getAllowedOperators,
        getFieldsWithoutAdvancedFilters,
        getHasAdvancedFilters,
    });

    const getActiveFields = useCallback(
        (filter) => {
            let activeFields = 0;
            const { id, fields, entity: crossEntity, isFakeCrossFilter } = filter;

            const finalEntity = !!crossEntity && !isFakeCrossFilter ? crossEntity : null;
            const ID = getCrossID(id, finalEntity);

            fields.forEach((current) => {
                const value = values?.[ID] || null;
                if (value) {
                    if (Array.isArray(value) && !rangeFields[current.dataType])
                        activeFields = value.length;
                    else activeFields = 1;
                }

                // There are fields that contain actions that activate other filters (p.e. activity's isSuspiciousCheckIn)
                // and we need to add thosee filters to the active fields count
                if (current.inputAttrs?.actions?.length) {
                    current.inputAttrs?.actions.forEach((action) => {
                        const actionID = getCrossID(action.id, finalEntity);
                        if (values[actionID]) {
                            activeFields = activeFields + 1;
                        }
                    });
                }
            });

            // Show 1 active field if only advanced filters 'Empty' or 'Not Empty' are active
            const currentOperators = !!finalEntity
                ? crossOperators?.[finalEntity] || {}
                : operators;
            const operator = currentOperators?.[id] ? currentOperators[id].value : undefined;

            if (activeFields === 0 && operator && [EMPTY, NOT_EMPTY].includes(operator))
                activeFields = 1;

            return activeFields;
        },
        [crossOperators, operators, values],
    );

    const getCommonProps = useCallback((field) => {
        return {
            label: field.label,
            labelMode: 'vertical',
            ...field.inputProps,
        };
    }, []);

    const getFieldProps = useCallback(
        ({ field, operator, crossEntity, onChange, onBlur, innerValues }) => {
            if (!field.inputProps) field.inputProps = {};

            const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
            const crossId = getCrossID(field.id, finalEntity);

            let commonProps = getCommonProps(field);

            let defaultValue = defaultValues[field.dataType];
            if (rangeFields[field.dataType]) defaultValue = defaultValues['range'];
            let finalValue = innerValues?.[crossId] || defaultValue;

            let propsByField = {
                ...commonProps,
                value: finalValue,
                onChange: onChange && onChange({ field, crossEntity: finalEntity }),
                isFullWidth: true,
            };

            const advancedFilters = getHasAdvancedFilters(finalEntity);

            const isDisabled =
                advancedFilters &&
                [
                    EMPTY,
                    NOT_EMPTY,
                    TODAY,
                    YESTERDAY,
                    TOMORROW,
                    THIS_WEEK,
                    LAST_WEEK,
                    NEXT_WEEK,
                    THIS_MONTH,
                    LAST_MONTH,
                    NEXT_MONTH,
                    THIS_QUARTER,
                    LAST_QUARTER,
                    NEXT_QUARTER,
                    THIS_YEAR,
                    LAST_YEAR,
                    NEXT_YEAR,
                ].includes(operator);

            switch (field.dataType) {
                case 'switch':
                    propsByField = {
                        ...propsByField,
                        checked: finalValue === true || finalValue === '1',
                        labelMode: 'horizontal',
                        isReadOnly: isDisabled,
                    };
                    delete propsByField.value;
                    break;
                case 'bool':
                    propsByField = {
                        ...propsByField,
                        placeholder: t('label_selectone'),
                        labelMode: 'horizontal',
                        isReadOnly: isDisabled,
                    };
                    break;
                case 'probability':
                    const isLessOperator = [LESS, LESS_OR_EQUAL].includes(operator);
                    const isGreaterOperator = [GREATER, GREATER_OR_EQUAL].includes(operator);
                    const isRange = !isLessOperator && !isGreaterOperator;
                    propsByField = {
                        ...propsByField,
                        min: 0,
                        max: 100,
                        step: 10,
                        isRange: advancedFilters ? isRange : true,
                        isReadOnly: isDisabled,
                        classes: { root: classes.slider },
                        tipFormatter: (value) => `${value}%`,
                    };
                    if (isLessOperator) {
                        propsByField.value = finalValue ? finalValue[0] : 0;
                    }
                    if (isGreaterOperator) {
                        propsByField.value = finalValue ? finalValue[1] : 100;
                    }
                    if (isRange) propsByField.value = finalValue;
                    break;
                case 'singleValueList':
                case 'multipleValueList':
                case 'relatedValueList':
                    propsByField = {
                        ...propsByField,
                        placeholder: t('label_selectone'),
                        isReadOnly: isDisabled,
                        highlightMatch: propsByField.isFuzzy,
                    };
                    break;
                case 'text':
                    propsByField.type = field.dataType;
                    propsByField.onBlur = onBlur && onBlur(field, finalEntity);
                    propsByField.onEnter = onBlur && onBlur(field, finalEntity);
                    propsByField.isReadOnly = isDisabled;
                    if (!propsByField.placeholder) {
                        propsByField.placeholder =
                            defaultPlaceholders[field.dataType] ||
                            t('placeholder_text_field') ||
                            '';
                    }
                    break;
                case 'singleDate':
                    propsByField = {
                        ...propsByField,
                        type: field.dataType,
                        isReadOnly: isDisabled,
                    };
                    break;
                default:
                    propsByField.type = field.dataType === 'quantity' ? 'integer' : field.dataType;
                    propsByField.onBlur = onBlur && onBlur(field, finalEntity);
                    propsByField.isReadOnly = isDisabled;
                    if (!propsByField.placeholder) {
                        propsByField.placeholder = defaultPlaceholders[propsByField.type] || '';
                    }
                    break;
            }

            if (isDisabled) {
                propsByField.placeholder = ['decimal', 'integer'].includes(field.dataType)
                    ? defaultPlaceholders[field.dataType]
                    : '';
            }

            if (field.isFilterReadOnly) {
                propsByField.isReadOnly = true;
            }

            return propsByField;
        },
        [getCommonProps, getHasAdvancedFilters, classes.slider, t],
    );

    const handleOnToggleFilter = useCallback(
        ({ filter, hasSubfilters, parentFilter }) => {
            return () => {
                onToggle && onToggle({ filter, hasSubfilters, parentFilter, fromQuickFilters });
            };
        },
        [fromQuickFilters, onToggle],
    );

    const onToggleQuickFilter = useCallback(
        (id) => {
            return (e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleQuickFilter(id);
            };
        },
        [toggleQuickFilter],
    );

    const renderField = useCallback(
        ({
            field,
            crossEntity,
            innerValues,
            handleChangeOperator,
            handleOnBlur,
            handleOnChange,
            handleOnChangeGroup,
            renderingSubfilters,
        }) => {
            if (!field.inputProps) field.inputProps = {};

            let FilterField;

            let defaultValue = defaultValues[field.dataType];

            if (rangeFields[field.dataType]) defaultValue = defaultValues['range'];

            const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
            const crossId = getCrossID(field.id, finalEntity);
            let finalValue = innerValues?.[crossId] || defaultValue;
            const advancedFilters = getHasAdvancedFilters(finalEntity);

            let operator = null;
            let OperatorField = null;

            if (hasAdvancedFilters({ ...field, crossEntity: finalEntity })) {
                operator = getFieldOperator(field, finalEntity);
                const {
                    options,
                    value,
                    defaultValue: operatorDefaultValue,
                } = getOperatorValues(field.dataType, operator, finalEntity);

                const isDefaultOperator = value?.value === operatorDefaultValue?.value;

                const overrides = isDefaultOperator
                    ? {
                          control: {
                              style: {
                                  borderColor: `${theme.colors.grey[100]} !important`,
                                  backgroundColor: theme.colors.primary.white,
                              },
                          },
                      }
                    : {
                          control: {
                              style: {
                                  borderColor: 'transparent !important',
                                  backgroundColor: theme.colors.semantic.infoCustom50,
                                  '&:hover': {
                                      backgroundColor: theme.colors.semantic.infoCustom100,
                                  },
                                  cursor: 'pointer',
                              },
                          },
                          controlFocused: {
                              style: {
                                  borderColor: 'transparent !important',
                                  backgroundColor: theme.colors.semantic.infoCustom100,
                              },
                          },
                          indicatorsContainer: {
                              style: {
                                  '& svg path': {
                                      fill: `${theme.colors.semantic.info500} !important`,
                                  },
                                  '& .hoi-poi-select__indicator-separator': {
                                      backgroundColor: theme.colors.semantic.info500,
                                  },
                              },
                          },
                          valueContainer: {
                              style: {
                                  '& .hoi-poi-select__single-value': {
                                      color: theme.colors.semantic.info500,
                                  },
                              },
                          },
                      };

                let operatorClearIcon = null;
                if (field.isFilterReadOnly !== true && !isDefaultOperator) {
                    operatorClearIcon = <Icon name="refresh" size="medium" />;
                }

                const operatorFieldProps = {
                    isRequired: isDefaultOperator && field.isFilterReadOnly !== true,
                    isSearchable: false,
                    isFullWidth: true,
                    className: 'fm-adv-filter-operator',
                    clearIcon: operatorClearIcon,
                    onChange:
                        field.isFilterReadOnly === true
                            ? undefined
                            : handleChangeOperator &&
                              handleChangeOperator({
                                  field,
                                  defaultValue: operatorDefaultValue,
                                  crossEntity: finalEntity,
                              }),
                    options,
                    value,
                    overrides,
                    isReadOnly: field.isFilterReadOnly === true,
                };

                OperatorField = <Select key={`${crossId}__operator`} {...operatorFieldProps} />;
            }

            if (field.overrideDefaultComponent) {
                const FieldComponent = customFieldsComponent[field.overrideDefaultComponent];

                if (!FieldComponent) return null;

                const fieldComponentProps = {
                    crossEntity: finalEntity,
                    field,
                    values: innerValues,
                    onChange: handleOnChange,
                    onBlur: handleOnBlur,
                    operator,
                };

                return (
                    <div key={crossId} className={classes.field}>
                        {OperatorField}
                        <FieldComponent key={crossId} {...fieldComponentProps} />
                    </div>
                );
            } else if (rangeFields[field.dataType]) {
                const Field = rangeFields[field.dataType];
                if (field.dataType === 'date') {
                    finalValue = [finalValue[0] || null, finalValue[1] || null];
                }

                let commonProps = getCommonProps(field);

                const commonRange = {
                    placeholder:
                        commonProps.placeholder ||
                        defaultPlaceholders[field.dataType] ||
                        t('action_select'),
                    ...(field.dataType === 'date'
                        ? {
                              calendarButtonLabel: t('label_today'),
                              lang: language,
                          }
                        : {}),
                };

                if (inputFields.includes(field.dataType)) {
                    commonRange.type = field.dataType;
                }

                const inputOneIsReadOnly =
                    advancedFilters &&
                    [
                        EMPTY,
                        NOT_EMPTY,
                        TODAY,
                        YESTERDAY,
                        TOMORROW,
                        THIS_WEEK,
                        LAST_WEEK,
                        NEXT_WEEK,
                        THIS_MONTH,
                        LAST_MONTH,
                        NEXT_MONTH,
                        THIS_QUARTER,
                        LAST_QUARTER,
                        NEXT_QUARTER,
                        THIS_YEAR,
                        LAST_YEAR,
                        NEXT_YEAR,
                        GREATER,
                        GREATER_OR_EQUAL,
                    ].includes(operator);

                const inputTwoIsReadOnly =
                    advancedFilters &&
                    [
                        EMPTY,
                        NOT_EMPTY,
                        TODAY,
                        YESTERDAY,
                        TOMORROW,
                        THIS_WEEK,
                        LAST_WEEK,
                        NEXT_WEEK,
                        THIS_MONTH,
                        LAST_MONTH,
                        NEXT_MONTH,
                        THIS_QUARTER,
                        LAST_QUARTER,
                        NEXT_QUARTER,
                        THIS_YEAR,
                        LAST_YEAR,
                        NEXT_YEAR,
                        LESS,
                        LESS_OR_EQUAL,
                    ].includes(operator);

                const inputPropsOne = {
                    ...commonProps,
                    ...commonRange,
                    className: field.locators?.first || null,
                    key: `${crossId}From`,
                    isReadOnly: inputOneIsReadOnly,
                    overrides: { flatpickrOptions: { position: 'auto center' } },
                };

                const inputPropsTwo = {
                    ...commonProps,
                    ...commonRange,
                    className: field.locators?.last || null,
                    key: `${crossId}To`,
                    isReadOnly: inputTwoIsReadOnly,
                    overrides: { flatpickrOptions: { position: 'auto center' } },
                };

                if (field.dataType !== 'date') {
                    const onBlurRange = handleOnBlur(field, finalEntity);
                    inputPropsOne.onBlur = (value) => {
                        if (value === undefined) {
                            onBlurRange();
                            return;
                        }
                        const newValue = [...finalValue];
                        newValue[0] = value;
                        onBlurRange(newValue, { changedIndex: 0 });
                    };
                    inputPropsTwo.onBlur = (value) => {
                        if (value === undefined) {
                            onBlurRange();
                            return;
                        }
                        const newValue = [...finalValue];
                        newValue[1] = value;
                        onBlurRange(newValue, { changedIndex: 1 });
                    };
                }

                const placeholderWhenDisabled = ['decimal', 'integer'].includes(field.dataType)
                    ? defaultPlaceholders[field.dataType]
                    : '';

                if (inputOneIsReadOnly) inputPropsOne.placeholder = placeholderWhenDisabled;
                if (inputTwoIsReadOnly) inputPropsTwo.placeholder = placeholderWhenDisabled;

                if (field.dataType === 'date') {
                    const minDate = finalValue[0] || null;
                    const maxDate = finalValue[1] || null;
                    inputPropsOne.maxDate = maxDate ? new Date(maxDate) : maxDate;
                    inputPropsTwo.minDate = minDate ? new Date(minDate) : minDate;
                    inputPropsOne.maxDate?.setHours(0, 0, 0, 0);
                    inputPropsTwo.minDate?.setHours(0, 0, 0, 0);

                    if (
                        advancedFilters &&
                        [
                            TODAY,
                            YESTERDAY,
                            TOMORROW,
                            THIS_WEEK,
                            LAST_WEEK,
                            NEXT_WEEK,
                            THIS_MONTH,
                            LAST_MONTH,
                            NEXT_MONTH,
                            THIS_QUARTER,
                            LAST_QUARTER,
                            NEXT_QUARTER,
                            THIS_YEAR,
                            LAST_YEAR,
                            NEXT_YEAR,
                        ].includes(operator)
                    ) {
                        finalValue = getRange(operator);
                        const currentValue = innerValues[crossId] || values[crossId];

                        // Trigger onChange to set the predefined filter values
                        // for the chosen operator if needed
                        if (
                            !currentValue ||
                            (currentValue &&
                                currentValue?.[0]?.toString?.() !==
                                    finalValue?.[0]?.toString?.()) ||
                            (currentValue &&
                                currentValue?.[1]?.toString?.() !== finalValue?.[1]?.toString?.())
                        ) {
                            handleOnChangeGroup(field, finalEntity)(finalValue);
                        }
                    }
                }

                return (
                    <div key={crossId} className={classes.field}>
                        {OperatorField}
                        <FieldGroup
                            key={crossId}
                            inputs={[Field, Field]}
                            isFullWidth
                            dividerText="-"
                            onChange={handleOnChangeGroup(field, finalEntity)}
                            value={finalValue}
                            inputProps={[inputPropsOne, inputPropsTwo]}
                        />
                    </div>
                );
            } else {
                const fieldProps = getFieldProps({
                    field,
                    operator,
                    crossEntity: finalEntity,
                    onChange: handleOnChange,
                    onBlur: handleOnBlur,
                    innerValues,
                });
                const FieldComponent = fieldsComponent[field.dataType] || null;
                let actionFields = null;

                if (field?.inputAttrs?.actions?.length) {
                    actionFields = (
                        <div className={classes.subFiltersActions} key={`${crossId}__actions`}>
                            {field.inputAttrs.actions.map((action) => {
                                const { id, component: Component, onClick, hint } = action;
                                const componentProps = { onClick, entity, hint };
                                return <Component key={id} {...componentProps} />;
                            })}
                        </div>
                    );
                }

                if (renderingSubfilters) {
                    // Inline menu inside subfilter sections (avoid clipping by section overflow)
                    if (
                        [
                            'bool',
                            'singleValueList',
                            'multipleValueList',
                            'relatedValueList',
                        ].includes(field.dataType)
                    ) {
                        fieldProps.useMenuPortal = false;
                        fieldProps.menuPosition = 'absolute';
                        fieldProps.overrides = {
                            styles: {
                                menu: (base) => ({
                                    ...base,
                                    position: 'relative',
                                    boxShadow: 'none !important',
                                    margin: 0,
                                    paddingBottom: 0,
                                }),
                                menuList: (base) => ({
                                    ...base,
                                    paddingBottom: 0,
                                    ...theme.utils.scrollbar,
                                }),
                            },
                            option: { style: { borderRadius: 4 } },
                        };
                    }
                }

                switch (field.dataType) {
                    case 'switch':
                        FilterField = (
                            <div className={classes.switchFilter}>
                                <Text size="small">{field.label}</Text>
                                <Switch
                                    checked={fieldProps.checked}
                                    onChange={fieldProps.onChange}
                                    isDisabled={fieldProps.isReadOnly}
                                />
                            </div>
                        );
                        break;
                    case 'bool':
                    case 'probability':
                    case 'singleValueList':
                    case 'multipleValueList':
                    case 'relatedValueList':
                    case 'text':
                    case 'singleDate':
                        FilterField = <FieldComponent {...fieldProps} />;
                        break;
                    default:
                        FilterField = <Input {...fieldProps} />;
                        break;
                }

                return (
                    <div key={crossId} className={classes.field}>
                        {OperatorField}
                        {FilterField}
                        {actionFields}
                    </div>
                );
            }
        },
        [
            classes.field,
            classes.switchFilter,
            classes.subFiltersActions,
            entity,
            getCommonProps,
            getFieldOperator,
            getFieldProps,
            getHasAdvancedFilters,
            getOperatorValues,
            hasAdvancedFilters,
            language,
            t,
            theme,
            values,
        ],
    );

    const renderFields = useCallback(
        ({ fields, ...otherProps }) => {
            return fields?.length > 0
                ? fields.map((field) => renderField({ ...otherProps, field }))
                : null;
        },
        [renderField],
    );

    const renderSubfilters = useCallback(({ renderer, className, schema, ...otherProps }) => {
        if (!schema?.length || !renderer) return null;
        return (
            <div className={className}>
                {renderer({ ...otherProps, schema, useSections: false, renderingSubfilters: true })}
            </div>
        );
    }, []);

    const renderFilters = useCallback(
        ({
            handleChangeOperator,
            handleOnBlur,
            handleOnChange,
            handleOnChangeGroup,
            handleOnDelete,
            innerValues,
            ...override
        }) => {
            const finalSchema = override.schema || schema;
            if (!finalSchema.length) return;

            const finalUseSections = override.hasOwnProperty('useSections')
                ? override.useSections
                : useSections;
            const finalToggled = override.toggled || toggled;
            const isSubfilter = override.renderingSubfilters;

            const content =
                finalSchema?.length > 0 &&
                finalSchema.map((filter, index) => {
                    let canBePinned = isPinnable;

                    if (filter.hasOwnProperty('isPinnable')) canBePinned = filter.isPinnable;

                    const ID = filter.crossId || filter.id;

                    const headerPostComponent = canBePinned ? (
                        <Tooltip content={t('label_filters_pin_desc')} placement="top">
                            <span>
                                <Icon
                                    className={classes.iconSection}
                                    name="pin"
                                    onClick={onToggleQuickFilter(ID)}
                                />
                            </span>
                        </Tooltip>
                    ) : undefined;

                    const hasSubfilters = !!filter.subfilterSchema;
                    let isOpen = false;

                    if (finalToggled.hasOwnProperty(ID)) {
                        const toggledFilter = finalToggled[ID];
                        isOpen =
                            typeof toggledFilter === 'boolean' ? toggledFilter : toggledFilter.open;
                    }

                    const renderProps = {
                        getFieldProps,
                        handleChangeOperator,
                        handleOnBlur,
                        handleOnChange,
                        handleOnChangeGroup,
                        innerValues,
                    };

                    const activeFields = getActiveFields(filter);

                    const sectionClasses = classNames(
                        classes.filter,
                        filter.className ? filter.className : `section-filter__field__${ID}`,
                        { [classes.subFilterHeader]: isSubfilter },
                    );
                    const titleLabel =
                        isSubfilter && activeFields > 0
                            ? `${filter.label} (${activeFields})`
                            : filter.label;

                    return (
                        <Section
                            key={index}
                            className={sectionClasses}
                            title={
                                <FilterTitle
                                    label={titleLabel}
                                    filterTooltip={filter.filterTooltip}
                                />
                            }
                            isOpen={isOpen}
                            onChange={handleOnToggleFilter({
                                filter: ID,
                                hasSubfilters,
                                parentFilter: override.parentFilter,
                            })}
                            activeFields={!isSubfilter && activeFields}
                            headerPostComponent={headerPostComponent}
                            onRemove={
                                filter.isRemovable
                                    ? handleOnDelete && handleOnDelete(filter)
                                    : undefined
                            }
                            classes={{
                                header: classes.filterHeader,
                                open: isOpen ? classes.filterHeaderOpen : null,
                            }}
                        >
                            {renderFields({
                                ...renderProps,
                                crossEntity: filter.entity,
                                fields: filter.fields,
                                renderingSubfilters: override?.renderingSubfilters,
                            })}
                            {renderSubfilters({
                                ...renderProps,
                                className: classes.subFilters,
                                parentFilter: ID,
                                renderer: renderFilters,
                                schema: filter.subfilterSchema,
                                toggled: finalToggled[ID]?.subfilters || {},
                                renderingSubfilters: true,
                            })}
                        </Section>
                    );
                });

            return finalUseSections ? (
                <FiltersSection
                    title={override.title || t('label_filters_section_filters')}
                    filterNum={finalSchema?.length}
                    hint={override.hint}
                >
                    {content}
                </FiltersSection>
            ) : (
                <Fragment>{content}</Fragment>
            );
        },
        [
            schema,
            useSections,
            toggled,
            t,
            isPinnable,
            classes.iconSection,
            classes.filter,
            classes.filterHeader,
            classes.filterHeaderOpen,
            classes.subFilters,
            classes.subFilterHeader,
            getFieldProps,
            handleOnToggleFilter,
            getActiveFields,
            renderFields,
            renderSubfilters,
            onToggleQuickFilter,
        ],
    );

    const hasOperatorsApplied = useMemo(() => {
        if (!operators && !crossOperators) return false;

        const checkOperators = (ops) => {
            const keys = Object.keys(ops || {});
            const arr = ['empty', 'notempty'];

            if (keys.length) {
                for (let key of keys) {
                    if (arr.includes(ops[key]?.value?.toLowerCase())) {
                        return true;
                    }
                }
            }
            return false;
        };

        // Check operators
        if (checkOperators(operators)) {
            return true;
        }

        // Check crossOperators
        for (let key in crossOperators) {
            if (checkOperators(crossOperators[key])) {
                return true;
            }
        }

        return false;
    }, [crossOperators, operators]);

    const hasFiltersApplied = useMemo(() => {
        if (
            (!values && !hasOperatorsApplied) ||
            (Object.keys(values).length === 0 && !hasOperatorsApplied)
        )
            return false;
        return true;
    }, [hasOperatorsApplied, values]);

    return {
        getActiveFields,
        getFieldProps,
        handleOnToggleFilter,
        hasFiltersApplied,
        onToggleQuickFilter,
        renderFields,
        renderFilters,
        renderSubfilters,
    };
};

export default useFilters;
